function normalizeSimpleContentItem(contentItem) {
  if (!contentItem || typeof contentItem !== 'object') {
    return null
  }

  if (contentItem.contentType || contentItem.__typename) {
    return contentItem
  }

  const hasLanguageToggleFields =
    contentItem.enUs !== undefined ||
    contentItem.esUs !== undefined ||
    contentItem.en_us !== undefined ||
    contentItem.es_us !== undefined
  if (hasLanguageToggleFields) {
    const hasEnglish = !!(contentItem.enUs ?? contentItem.en_us)
    const hasSpanish = !!(contentItem.esUs ?? contentItem.es_us)

    return {
      ...contentItem,
      enUs: hasEnglish,
      esUs: hasSpanish,
      contentType: {
        id: 'lang',
      },
      boundary:
        contentItem.boundary !== undefined
          ? contentItem.boundary
          : hasEnglish && hasSpanish,
    }
  }

  if (contentItem.asset) {
    return {
      ...contentItem,
      contentType: {
        id: 'textLinkIcon',
      },
    }
  }

  const hasSimpleCopy =
    !!contentItem.h2 ||
    !!contentItem.h3 ||
    !!contentItem.bodyCopy ||
    !!contentItem.text

  if (!hasSimpleCopy) {
    return contentItem
  }

  return {
    ...contentItem,
    contentType: {
      id: 'headerBodyCopy',
    },
    bodyCopy: contentItem.bodyCopy || contentItem.text || '',
    h2: contentItem.h2 || '',
    h3: contentItem.h3 || '',
  }
}

function normalizeActivityDetails(entry) {
  const normalizeActivity = (activity, index) => {
    return {
      ...activity,
      displayTitle:
        activity?.displayTitle ||
        activity?.title ||
        entry?.displayTitle ||
        `Activity ${index + 1}`,
      content: Array.isArray(activity?.content)
        ? activity.content
            .map(normalizeSimpleContentItem)
            .filter(contentItem => contentItem != null)
        : [],
    }
  }

  if (Array.isArray(entry?.activityDetails)) {
    return entry.activityDetails.map(normalizeActivity)
  }

  if (entry?.activityDetails && typeof entry.activityDetails === 'object') {
    return [normalizeActivity(entry.activityDetails, 0)]
  }

  if (Array.isArray(entry?.sections)) {
    return entry.sections.map(normalizeActivity)
  }

  if (Array.isArray(entry?.content)) {
    return [
      {
        displayTitle: entry?.displayTitle || 'Activity',
        content: entry.content
          .map(normalizeSimpleContentItem)
          .filter(contentItem => contentItem != null),
      },
    ]
  }

  return []
}

const DEFAULT_FACET_ID_BY_PROPERTY = {
  resourceType: 'metadataResourceType',
  duration: 'metadataDuration',
  frequency: 'metadataFrequency',
  grade: 'metadataGrade',
  audience: 'metadataAudience',
  unit: 'metadataUnit',
  goal: 'metadataGoal',
  learningCondition: 'metadataLearningCondition',
  academicSkill: 'metadataAcademicSkills',
  caselCompetency: 'metadataCasel',
  materials: 'metadataMaterials',
  programTheme: 'metadataProgramThemes',
}

function getFacetIdByPropertyFromSettings(facetMetadataSetting = {}) {
  const facets = [
    ...(facetMetadataSetting?.column1Facets || []),
    ...(facetMetadataSetting?.column2Facets || []),
  ]
  const mapping = {}
  facets.forEach(facet => {
    if (facet?.facetPropertyName && facet?.facetId) {
      mapping[facet.facetPropertyName] = facet.facetId
    }
  })
  return mapping
}

function getMetadataValuePropertyName(metadataValue = {}) {
  const ignoredFields = new Set([
    'contentType',
    '__typename',
    'id',
    'internalTitle',
    'locale',
    'type',
  ])
  const valueKeys = Object.keys(metadataValue).filter(
    key => !ignoredFields.has(key),
  )
  return valueKeys[0] || ''
}

function normalizeMetadataValues({
  metadataValues = [],
  facetMetadataSetting,
}) {
  const facetIdByPropertyName = {
    ...DEFAULT_FACET_ID_BY_PROPERTY,
    ...getFacetIdByPropertyFromSettings(facetMetadataSetting),
  }

  return metadataValues.map(metadataValue => {
    if (metadataValue?.contentType) {
      return metadataValue
    }
    const valueProperty = getMetadataValuePropertyName(metadataValue)
    const inferredFacetId = facetIdByPropertyName[valueProperty]
    if (!inferredFacetId) {
      return metadataValue
    }
    return {
      ...metadataValue,
      contentType: {
        id: inferredFacetId,
      },
    }
  })
}

function getMetadataCandidateQualityScore(metadataValues = []) {
  return metadataValues.reduce((score, metadataValue) => {
    const hasContentType =
      typeof metadataValue?.contentType === 'string' ||
      typeof metadataValue?.contentType?.id === 'string'
    const hasValueProperty = !!getMetadataValuePropertyName(metadataValue)

    return hasContentType || hasValueProperty ? score + 1 : score
  }, 0)
}

function pickBestMetadataValuesCandidate(metadataValueCandidates = []) {
  const arrayCandidates = metadataValueCandidates.filter(
    candidate => Array.isArray(candidate) && candidate.length > 0,
  )

  if (!arrayCandidates.length) {
    return []
  }

  return arrayCandidates.reduce((bestCandidate, currentCandidate) => {
    const bestScore = getMetadataCandidateQualityScore(bestCandidate)
    const currentScore = getMetadataCandidateQualityScore(currentCandidate)

    if (currentScore > bestScore) {
      return currentCandidate
    }

    return bestCandidate
  })
}

function normalizeResourceMetadata(entry) {
  const metadata = entry?.resourceMetadata || {}
  const metadataValueCandidates = [
    metadata?.metadataValues,
    metadata?.metadata,
    entry?.metadataValues,
    entry?.metadata,
  ]
  const metadataValues = pickBestMetadataValuesCandidate(
    metadataValueCandidates,
  )
  const normalizedMetadataValues = normalizeMetadataValues({
    metadataValues,
    facetMetadataSetting: entry?.facetMetadataSetting,
  })

  return {
    ...metadata,
    displayTitle: metadata?.displayTitle || entry?.displayTitle || '',
    description: metadata?.description || entry?.description || '',
    metadataValues: normalizedMetadataValues,
    courseNodeConnector:
      metadata?.courseNodeConnector || entry?.courseNodeConnector || {},
    routeSlug: metadata?.routeSlug || entry?.routeSlug || '',
    time: metadata?.time || entry?.time || '',
    duration: metadata?.duration || entry?.duration || '',
  }
}

const DEFAULT_FACET_LAYOUT = {
  column1Label: 'Activity',
  column1Facets: [
    {
      displayTitle: 'Type: ',
      facetId: 'metadataResourceType',
      facetPropertyName: 'resourceType',
    },
    {
      displayTitle: 'Duration: ',
      facetId: 'metadataDuration',
      facetPropertyName: 'duration',
    },
    {
      displayTitle: 'Frequency: ',
      facetId: 'metadataFrequency',
      facetPropertyName: 'frequency',
    },
    {
      displayTitle: 'Suggested Grade: ',
      facetId: 'metadataGrade',
      facetPropertyName: 'grade',
    },
    {
      displayTitle: 'Audience: ',
      facetId: 'metadataAudience',
      facetPropertyName: 'audience',
    },
  ],
  column2Label: 'Learning',
  column2Facets: [
    {
      displayTitle: 'Pathway: ',
      facetId: 'metadataUnit',
      facetPropertyName: 'unit',
    },
    {
      displayTitle: 'Goal: ',
      facetId: 'metadataGoal',
      facetPropertyName: 'goal',
    },
    {
      displayTitle: 'Learning Condition: ',
      facetId: 'metadataLearningCondition',
      facetPropertyName: 'learningCondition',
    },
    {
      displayTitle: 'Academic Skills: ',
      facetId: 'metadataAcademicSkills',
      facetPropertyName: 'academicSkill',
    },
    {
      displayTitle: 'CASEL Alignment: ',
      facetId: 'metadataCasel',
      facetPropertyName: 'caselCompetency',
    },
  ],
}

function getFacetId(metadataValue) {
  const contentType = metadataValue?.contentType
  if (typeof contentType === 'string') {
    return contentType
  }
  return contentType?.id || ''
}

function normalizeFacetMetadataSetting({
  facetMetadataSetting,
  metadataValues,
}) {
  if (
    facetMetadataSetting &&
    (Array.isArray(facetMetadataSetting?.column1Facets) ||
      Array.isArray(facetMetadataSetting?.column2Facets))
  ) {
    return facetMetadataSetting
  }

  if (!Array.isArray(metadataValues) || metadataValues.length === 0) {
    return {}
  }

  const availableFacetIds = new Set(metadataValues.map(getFacetId))
  const column1Facets = DEFAULT_FACET_LAYOUT.column1Facets.filter(facet =>
    availableFacetIds.has(facet.facetId),
  )
  const column2Facets = DEFAULT_FACET_LAYOUT.column2Facets.filter(facet =>
    availableFacetIds.has(facet.facetId),
  )

  if (!column1Facets.length && !column2Facets.length) {
    return {}
  }

  return {
    column1Label: DEFAULT_FACET_LAYOUT.column1Label,
    column1Facets,
    column2Label: DEFAULT_FACET_LAYOUT.column2Label,
    column2Facets,
  }
}

export function isIndividualResourceEntry({
  pageEntry,
  queryType,
  curriculumRoute,
}) {
  if (!pageEntry || typeof pageEntry !== 'object') {
    return false
  }

  if (queryType === 'learningExperience') {
    return true
  }

  const routePathCandidates = [
    curriculumRoute?.route,
    curriculumRoute?.path,
    curriculumRoute?.routePath,
    curriculumRoute?.resolvedPath,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (
    routePathCandidates.includes('/lessons/') &&
    routePathCandidates.includes('/teach/')
  ) {
    return true
  }

  const hasTabSections =
    Array.isArray(pageEntry?.sections) &&
    pageEntry.sections.some(section => {
      return String(section?.type || '').toLowerCase() === 'tab'
    })

  return hasTabSections && !!pageEntry?.presentation
}

export function transformIndividualResourceEntry(entry) {
  const resourceMetadata = normalizeResourceMetadata(entry)
  const facetMetadataSetting = normalizeFacetMetadataSetting({
    facetMetadataSetting: entry?.facetMetadataSetting,
    metadataValues: resourceMetadata?.metadataValues,
  })

  return {
    ...entry,
    activityDetails: normalizeActivityDetails(entry),
    facetMetadataSetting,
    references: typeof entry?.references === 'string' ? entry.references : null,
    resourceMetadata,
    surveyLink: entry?.surveyLink || null,
    languageToggle: entry?.languageToggle || null,
    additionalResources: Array.isArray(entry?.additionalResources)
      ? entry.additionalResources
      : [],
    // The riseActivity link drives the experience-player launch button. It
    // carries `filename` (used as the experience-player lessonId) and
    // `expiration` (used to derive the service `expireAt` timestamp).
    // TODO [curriculum-service]: the curriculum service GraphQL query does not
    // yet resolve/return the `riseActivity` reference (filename + expiration)
    // for learningExperience entries. Add it there so this passes through with
    // real data instead of being null in production.
    riseActivity: entry?.riseActivity || null,
  }
}
